import React, { useState } from "react";
import PageLayout from "../components/layout/PageLayout";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import CTASection from "../components/shared/CTASection";
import Mandala from "../components/decor/Mandala";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { DEITIES } from "../data/deities";
import { IMG } from "../data/site";
import { Sparkles, Clock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const Deities = () => {
  const { t, lang } = useLang();
  const pick = (d, key) => (lang === "kn" && d[`${key}Kn`]) || d[key];
  const copy = {
    awaitingDarshana: lang === "kn" ? "ದರ್ಶನ ಚಿತ್ರ ನಿರೀಕ್ಷೆಯಲ್ಲಿದೆ" : "Awaiting Darshana",
    imageComingSoon: lang === "kn" ? "ಪವಿತ್ರ ಮೂರ್ತಿಯ ಚಿತ್ರವನ್ನು ಶೀಘ್ರದಲ್ಲೇ ಸೇರಿಸಲಾಗುತ್ತದೆ" : "The sacred icon image will be added shortly",
    experienceSode: lang === "kn" ? "ಸೋದೆ ಅನುಭವಿಸಿ" : "Experience Sode",
    planDarshana: lang === "kn" ? "ವೈಯಕ್ತಿಕ ದರ್ಶನಕ್ಕಾಗಿ ಭೇಟಿಯನ್ನು ಯೋಜಿಸಿ" : "Plan a visit for personal darshana",
    templeTimings: lang === "kn" ? "ದೇವಸ್ಥಾನ ಸಮಯಗಳು" : "Temple Timings",
    reachSode: lang === "kn" ? "ಸೋದೆಗೆ ತಲುಪುವ ಮಾರ್ಗ" : "How to reach Sode",
  };
  const [openDeity, setOpenDeity] = useState(null);

  return (
    <PageLayout>
      {/* Gallery - mirrors Home "Divine deities of Sode Matha" design */}
      <section
        className="py-20 sm:py-16 relative"
        data-testid="deities-gallery"
        style={{ background: "linear-gradient(180deg, #E4D3B1 0%, #DFCCA6 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={t("deities.hero.eyebrow")}
            title={t("deities.hero.title")}
            subtitle={t("deities.hero.subtitle")}
            align="center"
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-fr">
            {DEITIES.map((d) => (
              <button
                key={d.slug}
                onClick={() => setOpenDeity(d)}
                className="group text-left relative rounded-2xl overflow-hidden border border-[#D4AF37]/30 card-lift bg-[#FBF7F0] flex flex-col h-full"
                data-testid={`deity-btn-${d.slug}`}
              >
                <div className="aspect-square overflow-hidden relative shrink-0">
                  {d.isIdol ? (
                    <div className="absolute inset-0 bg-gradient-to-b from-[#FBF7F0] via-[#F2E7D5] to-[#E4D3B1] flex items-center justify-center">
                      <div className="absolute inset-0 flex items-center justify-center opacity-45 animate-mandala-slow">
                        <Mandala size={280} color="#B65A14" opacity={0.5} />
                      </div>
                      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 45%, rgba(201,162,39,0.30) 0%, transparent 65%)" }} />
                      {d.image ? (
                        <img
                          src={d.image}
                          alt={d.name}
                          className={`relative z-10 object-contain transition-transform duration-700 group-hover:scale-105 ${d.slug === "bhuvaraha" ? "max-h-[62%] max-w-[52%]" : "max-h-[100%] max-w-[100%]"}`}
                          style={{ filter: "drop-shadow(0 10px 22px rgba(51, 36, 28, 0.4))" }}
                        />
                      ) : (
                        <div className="relative z-10 text-center px-4">
                          <div className="w-20 h-20 mx-auto rounded-full border-2 border-[#B65A14]/60 flex items-center justify-center mb-3 bg-[#FBF7F0]/80">
                            <Sparkles className="w-8 h-8 text-[#B65A14]" />
                          </div>
                          <div className="font-serif-display text-base text-[#B65A14]">{copy.awaitingDarshana}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#3B2A1A] via-[#3B2A1A]/30 to-transparent" />
                    </>
                  )}
                </div>
                <div className="bg-gradient-to-b from-[#B65A14] to-[#8F4610] px-4 py-3 relative z-20 h-[104px] flex flex-col items-center justify-center" data-testid={`deity-caption-${d.slug}`}>
                  <div className="font-cinzel text-xs tracking-widest text-[#D4AF37] mb-0.5 text-center">{d.sanskrit}</div>
                  <div className="font-serif-display text-xl sm:text-2xl text-[#FBF7F0] font-semibold leading-tight text-center">{pick(d, "name")}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!openDeity} onOpenChange={(o) => !o && setOpenDeity(null)}>
        <DialogContent
          className="w-[calc(100vw-1.5rem)] max-w-3xl max-h-[calc(100dvh-1.5rem)] bg-[#FBF7F0] border-[#D4AF37]/40 p-0 overflow-y-auto overflow-x-hidden [&>button]:z-50 [&>button]:rounded-full [&>button]:bg-[#3B2A1A]/85 [&>button]:p-2 [&>button]:text-[#FBF7F0] [&>button]:opacity-100 [&>button]:shadow-lg [&>button]:ring-offset-[#FBF7F0]"
          data-testid="deity-dialog"
        >
          {openDeity && (
            <>
              <div className="grid md:grid-cols-2">
                <div className="relative aspect-square md:aspect-auto md:min-h-[440px]">
                  {openDeity.isIdol ? (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#FBF7F0] via-[#F2E7D5] to-[#E4D3B1] flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center opacity-45 animate-mandala-slow">
                        <Mandala size={420} color="#B65A14" opacity={0.6} />
                      </div>
                      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 45%, rgba(201,162,39,0.35) 0%, transparent 60%)" }} />
                      {openDeity.image ? (
                        <img
                          src={openDeity.image}
                          alt={openDeity.name}
                          className={`relative z-10 object-contain ${openDeity.slug === "bhuvaraha" ? "max-h-[64%] max-w-[55%]" : "max-h-[90%] max-w-[88%]"}`}
                          style={{ filter: "drop-shadow(0 12px 30px rgba(51,36,28,0.45))" }}
                        />
                      ) : (
                        <div className="relative z-10 text-center px-8">
                          <div className="w-28 h-28 mx-auto rounded-full border-2 border-[#B65A14]/60 flex items-center justify-center mb-5 bg-[#FBF7F0]/80">
                            <Sparkles className="w-12 h-12 text-[#B65A14]" />
                          </div>
                          <div className="font-serif-display text-2xl text-[#B65A14]">{copy.awaitingDarshana}</div>
                          <div className="text-[11px] uppercase tracking-widest text-[#3B2A1A]/70 mt-2">{copy.imageComingSoon}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <img src={openDeity.image} alt={openDeity.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#3B2A1A]/60 to-transparent" />
                    </>
                  )}
                </div>
                <div className="p-8 relative">
                  <div className="absolute -top-6 -right-6 opacity-20"><Mandala size={140} color="#D4AF37" opacity={0.9} /></div>
                  <DialogHeader>
                    <div className="font-cinzel text-xs tracking-widest text-[#B65A14]">{openDeity.sanskrit}</div>
                    <DialogTitle className="font-serif-display text-3xl text-[#3B2A1A] leading-tight mt-1">{pick(openDeity, "name")}</DialogTitle>
                    <DialogDescription className="text-[#B65A14] italic mt-1">{pick(openDeity, "tagline")}</DialogDescription>
                  </DialogHeader>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#B65A14]"><Clock className="w-3 h-3" /> {pick(openDeity, "timing")}</div>
                  <div className="mt-5 gold-underline" />
                  <p className="mt-5 text-sm text-[#3B2A1A]/85 leading-relaxed">{pick(openDeity, "intro")}</p>
                  <div className="mt-5 p-4 rounded-xl bg-[#F2E7D5]/60 border border-[#D4AF37]/30">
                    <div className="overline text-[#B65A14] mb-2">{lang === "kn" ? "ಮಹತ್ವ" : "Significance"}</div>
                    <p className="text-sm text-[#3B2A1A]/85 leading-relaxed">{pick(openDeity, "significance")}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <SectionDivider variant="lotus" />

      <CTASection
        eyebrow={copy.experienceSode}
        title={copy.planDarshana}
        primary={{ label: copy.templeTimings, to: "/events" }}
        secondary={{ label: copy.reachSode, to: "/contact" }}
        image={IMG.templeCorridor}
      />
    </PageLayout>
  );
};

export default Deities;
